import { IntlShape } from 'react-intl';
import { KeyboardLayoutObject, KeyboardOptions, SimpleKeyboard } from 'react-simple-keyboard';

import Language from 'types/LanguageEnum';
import { createDictionary } from 'utils/objectUtils';

import greekLayout from '../layouts/greek';
import danishLayout from '../layouts/danish';
import frenchLayout from '../layouts/french';
import germanLayout from '../layouts/german';
import italianLayout from '../layouts/italian';
import spanishLayout from '../layouts/spanish';
import swedishLayout from '../layouts/swedish';
import russianLayout from '../layouts/russian';
import croatianLayout from '../layouts/croatian';
import estonianLayout from '../layouts/estonian';
import romanianLayout from '../layouts/romanian';
import englishUKLayout from '../layouts/englishUK';
import englishUSLayout from '../layouts/englishUS';
import norwegianLayout from '../layouts/norwegian';
import ukrainianLayout from '../layouts/ukrainian';
import lithuanianLayout from '../layouts/lithuanian';
import portugueseLayout from '../layouts/portuguese';
import serbianLayout from '../layouts/serbian';

// Special layouts
import numberLayout from '../layouts/specials/number';
import telLayout from '../layouts/specials/tel';

// Japanese
import japaneseLayout from '../Custom/Japanese/layouts/numbers';
import abcLayout from '../Custom/Japanese/layouts/abc';
import numbersLayout from '../Custom/Japanese/layouts/numbers';
import hiraganaLayout from '../Custom/Japanese/layouts/hiragana';

import { ACCENT_CANDIDATES } from './accentCandidates';
import { DEFAULT_THEME, SPECIAL_KEYS } from './constants';


const shiftKey =
  '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_2_10)"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.2985 5.24308C11.1746 4.17415 12.8254 4.17415 13.7015 5.24308L19.383 12.1744C19.4849 12.2988 19.5405 12.4539 19.5405 12.614C19.5405 12.9998 19.2238 13.3126 18.8331 13.3126H15.8919C15.2202 13.3126 14.6757 13.8504 14.6757 14.5138V18.8379C14.6757 19.236 14.349 19.5586 13.9459 19.5586H10.0541C9.65104 19.5586 9.32432 19.236 9.32432 18.8379V14.5138C9.32432 13.8504 8.7798 13.3126 8.10811 13.3126H5.16688C4.77618 13.3126 4.45946 12.9998 4.45946 12.614C4.45946 12.4539 4.51509 12.2988 4.61704 12.1744L10.2985 5.24308ZM14.8359 4.33617C13.3756 2.55461 10.6244 2.55461 9.16411 4.33617L3.48268 11.2675C3.17039 11.6485 3 12.1238 3 12.614C3 13.7959 3.97014 14.754 5.16688 14.754H7.86486V18.8379C7.86486 20.032 8.845 21 10.0541 21H13.9459C15.155 21 16.1351 20.032 16.1351 18.8379V14.754H18.8331C20.0299 14.754 21 13.7959 21 12.614C21 12.1238 20.8296 11.6485 20.5173 11.2675L14.8359 4.33617Z" fill="#1D252D"/></g></svg>';

const bkspKey =
  '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_0_3)"><path d="M10.71 10.53L14.71 14.53C15 14.82 15.47 14.82 15.77 14.53C16.06 14.23 16.06 13.76 15.77 13.46L11.77 9.46C11.47 9.16 11 9.16 10.7 9.46C10.4 9.75 10.4 10.22 10.7 10.52L10.71 10.53Z" fill="#1D252D"/><path d="M14.71 9.46L10.71 13.46C10.41 13.75 10.41 14.22 10.71 14.52C11 14.81 11.47 14.81 11.77 14.52L15.77 10.52C16.06 10.22 16.06 9.75 15.77 9.45C15.47 9.15 15 9.15 14.7 9.45L14.71 9.46Z" fill="#1D252D"/><path d="M7.41 17.29L4.39 13.74C3.52 12.72 3.52 11.24 4.38 10.23L7.39 6.68C7.9 6.07 8.65 5.72 9.45 5.72H17.51C19 5.72 20.21 6.93 20.21 8.42V15.5C20.21 16.99 18.99 18.2 17.5 18.2H9.43C8.63 18.2 7.88 17.85 7.36 17.24L7.41 17.29ZM6.26 18.26C7.05 19.19 8.23 19.74 9.46 19.73H17.52C19.84 19.73 21.72 17.84 21.72 15.52V8.43C21.72 6.1 19.83 4.22 17.51 4.22H9.44C8.2 4.21 7.03 4.76 6.23 5.69L3.21 9.23C1.87 10.8 1.87 13.11 3.21 14.68L6.22 18.22L6.26 18.26Z" fill="#1D252D"/></g></svg>';

const enterKey =
  '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <g clip-path="url(#clip0_0_3)"><path d="M19.3175 6.9081L19.3175 10.5405C19.3175 11.376 18.6516 12.054 17.804 12.054L3.27437 12.054C2.77794 12.054 2.36627 12.4536 2.36627 12.9621C2.36627 13.4586 2.77794 13.8702 3.27437 13.8702L17.804 13.8702C19.6444 13.8702 21.1337 12.3688 21.1337 10.5405L21.1337 6.9081C21.1337 6.39957 20.7342 6 20.2256 6C19.7292 6 19.3175 6.39957 19.3175 6.9081Z" fill="#1D252D" /><path d="M6.26508 8.67592L2.63267 12.3083C2.28153 12.6595 2.28153 13.2285 2.63267 13.5918L6.26508 17.2242C6.62832 17.5753 7.1974 17.5753 7.56064 17.2242C7.92388 16.861 7.92388 16.2919 7.56064 15.9286L3.92823 12.2962L3.92823 13.5797L7.56064 9.94726C7.92388 9.58402 7.92388 9.01494 7.56064 8.6517C7.2095 8.28846 6.64043 8.28846 6.27719 8.6517L6.26508 8.67592Z" fill="#1D252D" /></g><defs><clipPath id="clip0_0_3"><rect width="24" height="24" fill="#1D252D"/></clipPath></defs></svg>';

const globeKey =
  '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g fill="#323232"><path d="M12 3.75c4.56 0 8.25 3.68 8.25 8.25 0 4.56-3.69 8.25-8.25 8.25 -.42 0-.75.33-.75.75 0 .41.33.75.75.75 5.39 0 9.75-4.36 9.75-9.75 0-5.4-4.36-9.75-9.75-9.75 -.42 0-.75.33-.75.75 0 .41.33.75.75.75Z"/><path d="M12 20.25c-4.57 0-8.25-3.69-8.25-8.25 0-4.57 3.68-8.25 8.25-8.25 .41 0 .75-.34.75-.75 0-.42-.34-.75-.75-.75 -5.4 0-9.75 4.35-9.75 9.75 0 5.39 4.35 9.75 9.75 9.75 .41 0 .75-.34.75-.75 0-.42-.34-.75-.75-.75Z"/><path d="M9.52 3.67c-3.04 5.06-3.04 11.58-.001 16.65 1.13 1.89 3.81 1.89 4.95-.01 3.03-5.07 3.03-11.59-.01-16.66 -1.14-1.9-3.82-1.9-4.96 0Zm1.28.77c.55-.93 1.82-.93 2.37 0 2.74 4.58 2.74 10.52-.01 15.11 -.56.92-1.83.92-2.38 0 -2.75-4.59-2.75-10.53-.01-15.12Z"/><path d="M3.78 8.57c5.03 1.55 11.38 1.55 16.42-.01 .39-.13.61-.55.49-.94 -.13-.4-.55-.62-.94-.5C15 8.58 8.96 8.58 4.21 7.11c-.4-.13-.82.09-.94.49 -.13.39.09.81.49.93Z"/><path d="M20.21 15.42c-5.04-1.56-11.39-1.56-16.43 0 -.4.12-.62.54-.5.93s.54.61.93.49c4.74-1.47 10.78-1.47 15.53 0 .39.12.81-.1.93-.5s-.1-.82-.5-.94Z"/></g></svg>';

const smallKey =
  '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.86198 10.2044H10.7375V18.8732C10.7375 19.1686 10.6943 19.3884 10.6078 19.5325C10.5213 19.6766 10.3808 19.7811 10.1863 19.846C9.98448 19.9108 9.71066 19.9541 9.36477 19.9757C9.02609 19.9973 8.60454 20.0045 8.10012 19.9973C8.0857 19.9252 8.05688 19.8352 8.01364 19.7271C7.97761 19.6262 7.93798 19.5253 7.89475 19.4244C7.85151 19.3235 7.80827 19.2335 7.76504 19.1542C8.03166 19.1614 8.28747 19.165 8.53248 19.165C8.77748 19.1722 8.99006 19.1722 9.17021 19.165C9.35756 19.165 9.48727 19.165 9.55933 19.165C9.66742 19.1578 9.74308 19.1362 9.78632 19.1002C9.83676 19.0569 9.86198 18.9813 9.86198 18.8732V10.2044ZM11.9805 12.9607L12.7588 12.6688C13.0831 13.1732 13.3893 13.7137 13.6776 14.2902C13.9658 14.8666 14.218 15.4323 14.4342 15.9872C14.6576 16.542 14.8161 17.0356 14.9098 17.468L14.0343 17.8247C13.955 17.3923 13.8109 16.8987 13.6019 16.3439C13.4001 15.7818 13.1551 15.2089 12.8669 14.6252C12.5858 14.0343 12.2904 13.4795 11.9805 12.9607ZM7.03003 12.7445L7.93798 12.8958C7.81548 13.4218 7.66415 13.9767 7.484 14.5604C7.30385 15.1369 7.08767 15.6953 6.83546 16.2358C6.58325 16.769 6.29141 17.2374 5.95994 17.6409C5.89508 17.5905 5.81582 17.5401 5.72214 17.4896C5.63567 17.432 5.54199 17.3779 5.4411 17.3275C5.34743 17.277 5.26456 17.2374 5.1925 17.2086C5.51677 16.8267 5.7978 16.3799 6.0356 15.8683C6.2734 15.3566 6.47516 14.8306 6.6409 14.2902C6.81385 13.7425 6.94355 13.2273 7.03003 12.7445Z" fill="black"/><path d="M16.1961 5.72512C16.1961 5.44841 16.2609 5.19764 16.3906 4.97281C16.5203 4.74798 16.6976 4.57072 16.9224 4.44101C17.1472 4.30265 17.398 4.23347 17.6747 4.23347C17.9514 4.23347 18.2022 4.30265 18.427 4.44101C18.6519 4.57072 18.8291 4.74798 18.9588 4.97281C19.0885 5.19764 19.1534 5.44841 19.1534 5.72512C19.1534 5.99318 19.0885 6.23962 18.9588 6.46445C18.8291 6.68928 18.6519 6.87087 18.427 7.00922C18.2022 7.13893 17.9514 7.20379 17.6747 7.20379C17.398 7.20379 17.1472 7.13893 16.9224 7.00922C16.6976 6.87087 16.5203 6.68928 16.3906 6.46445C16.2609 6.23962 16.1961 5.99318 16.1961 5.72512ZM16.7538 5.72512C16.7538 5.95859 16.8446 6.17045 17.0262 6.36068C17.2164 6.54228 17.4326 6.63307 17.6747 6.63307C17.9168 6.63307 18.1287 6.54228 18.3103 6.36068C18.4919 6.17045 18.5827 5.95859 18.5827 5.72512C18.5827 5.48299 18.4919 5.27114 18.3103 5.08955C18.1287 4.90796 17.9168 4.81716 17.6747 4.81716C17.4326 4.81716 17.2164 4.90796 17.0262 5.08955C16.8446 5.27114 16.7538 5.48299 16.7538 5.72512Z" fill="black"/><path d="M5.58595 4.63557C5.69836 4.79122 5.83239 5.00307 5.98804 5.27114C6.15234 5.53055 6.31231 5.79429 6.46796 6.06236C6.63226 6.33042 6.75764 6.55092 6.84411 6.72387L6.09181 7.04814C5.99669 6.8579 5.8713 6.62875 5.71565 6.36068C5.56865 6.09262 5.41733 5.82888 5.26168 5.56947C5.10603 5.3014 4.96767 5.08955 4.84661 4.9339L5.58595 4.63557ZM7.22027 4C7.33268 4.15565 7.46671 4.36751 7.62236 4.63557C7.78666 4.89498 7.94663 5.15872 8.10228 5.42679C8.26658 5.6862 8.39628 5.90671 8.4914 6.0883L7.7391 6.3996C7.64398 6.20936 7.51859 5.98453 7.36294 5.72512C7.21594 5.45705 7.06462 5.19331 6.90897 4.9339C6.75332 4.66583 6.61064 4.4583 6.48093 4.3113L7.22027 4Z" fill="black"/></svg>';

export const WRITABLE_INPUT_TYPES = ['textarea', 'email', 'text', 'number', 'search', 'url', 'tel', 'password'];

type KeyboardLayout = {
  layout: KeyboardLayoutObject;
  layoutCandidates?: { [key: string]: string };
};

type WritableInputTypesConfig = { excludedKeys?: KeyboardOptions['excludeFromLayout']; layout?: KeyboardLayoutObject };

export type CandidatesKeyboard = SimpleKeyboard & {
  flushCandidates?: () => void;
  refreshCandidates?: () => void;
  setInputRange?: (input: string) => void;
  updateCandidates?: (input: string) => Promise<void>;
  onCandidatesChange?: (hasCandidates: boolean) => void;
  selectNextCandidate?: () => string | null;
  confirmSelectedCandidate?: () => void;
};

export const BUTTON_LABELS = (intl: IntlShape) => ({
  [SPECIAL_KEYS.SHIFT]: shiftKey,
  [SPECIAL_KEYS.SPACE]: intl.formatMessage({ id: 'keyboard.keys.space', defaultMessage: 'Space' }),
  [SPECIAL_KEYS.BACKSPACE]: bkspKey,
  [SPECIAL_KEYS.ENTER]: enterKey,
  [SPECIAL_KEYS.NEXT_KEYBOARD]: globeKey,

  [SPECIAL_KEYS.CONFIRM]: '確定',
  [SPECIAL_KEYS.NEXT_CANDIDATE]: '次候補',
  // Japanese Kana keyboard keys
  [SPECIAL_KEYS.NUMBERS]: '☆123',
  [SPECIAL_KEYS.ABC]: 'ABC',
  [SPECIAL_KEYS.KANA]: 'あいう',
  [SPECIAL_KEYS.SMALL]: smallKey,
  [SPECIAL_KEYS.ZENKAKU]: '全角',
  [SPECIAL_KEYS.DIVIDER]: '',
  [SPECIAL_KEYS.BRACKETS]: '「」',
  [SPECIAL_KEYS.KAOMOJI]: '^^',
  // Previous version
  [SPECIAL_KEYS.CONVERT]: 'カナ',
  [SPECIAL_KEYS.CONVERT_HIRAGANA]: 'あいう',
});

const DEFAULT_KEYBOARD = englishUSLayout;

const DEFAULT_LAYOUT = DEFAULT_KEYBOARD.layout;

export const BASE_KEYBOARD_BY_LANGUAGE: Partial<Record<Language, KeyboardLayout>> = createDictionary(
  {
    [Language.DA_DK]: danishLayout,
    [Language.DE]: germanLayout,
    [Language.EL_GR]: greekLayout,
    [Language.EN_GB]: englishUKLayout,
    [Language.ES]: spanishLayout,
    [Language.ET_EE]: estonianLayout,
    [Language.FI_FI]: swedishLayout,
    [Language.FR]: frenchLayout,
    [Language.HR_HR]: croatianLayout,
    [Language.IT]: italianLayout,
    [Language.JA_JP]: japaneseLayout,
    [Language.LT_LT]: lithuanianLayout,
    [Language.NO]: norwegianLayout,
    [Language.PT]: portugueseLayout,
    [Language.RO_RO]: romanianLayout,
    [Language.SV]: swedishLayout,
    [Language.RU]: russianLayout,
    [Language.UK]: ukrainianLayout,
    [Language.SR_RS]: serbianLayout,
  },
  DEFAULT_KEYBOARD,
);

export const LAYOUTS_BY_LANGUAGE: Partial<Record<Language, Array<KeyboardLayoutObject>>> = {
  [Language.JA_JP]: [numbersLayout.layout, abcLayout.layout, hiraganaLayout.layout],
  [Language.RU]: [russianLayout.layout, DEFAULT_LAYOUT],
  [Language.UK]: [ukrainianLayout.layout, DEFAULT_LAYOUT],
};

const EMAIL_KEYBOARD_BY_LANGUAGE = {
  ...BASE_KEYBOARD_BY_LANGUAGE,
  [Language.UK]: DEFAULT_KEYBOARD,
  [Language.RU]: DEFAULT_KEYBOARD,
  [Language.JA_JP]: DEFAULT_KEYBOARD,
};

export enum KeyboardLayoutType {
  DEFAULT = 'default',
  ALTERNATE = 'alternate',
}

export const OPTIONS_BY_LANG: Partial<Record<Language, { theme: string; layoutCandidatesPageSize: number }>> =
  createDictionary(
    {
      [Language.JA_JP]: {
        theme: 'paginated-candidates',
        layoutCandidatesPageSize: 5,
      },
    },
    { theme: DEFAULT_THEME, layoutCandidatesPageSize: 10 },
  );

export const getWritableInputTypesConfig = (language: Language) =>
  createDictionary<string, WritableInputTypesConfig>(
    {
      tel: telLayout,
      number: numberLayout,
      email: { layout: EMAIL_KEYBOARD_BY_LANGUAGE[language]?.layout },
    },
    { excludedKeys: { default: [SPECIAL_KEYS.ENTER], shift: [SPECIAL_KEYS.ENTER] } },
  );

export const getLayoutsByLanguage = (language: Language) =>
  LAYOUTS_BY_LANGUAGE[language] ?? [BASE_KEYBOARD_BY_LANGUAGE[language].layout];

export const getDefaultCandidates = (language: Language) => ({
  ...ACCENT_CANDIDATES,
  ...BASE_KEYBOARD_BY_LANGUAGE[language].layoutCandidates,
});

export const getPortalContainer = () => {
  let portal = document.getElementById('keyboard-portal');

  if (!portal) {
    portal = document.createElement('div');
    portal.id = 'keyboard-portal';
    portal.className = 'fixed z-60';
    document.body.appendChild(portal);
  }
  return portal;
};

export const isSpecialKey = (key: string) => Object.values(SPECIAL_KEYS).includes(key as SPECIAL_KEYS);

export const hasKey = (layout: string[], keyToCheck: string): boolean =>
  layout.some(row => row.split(' ').includes(keyToCheck));

export const isAccentKey = (key: string) => Object.keys(ACCENT_CANDIDATES).includes(key);
